import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  FileText,
  TrendingUp,
  DollarSign,
  FileSpreadsheet,
  BarChart3,
  Users,
} from "lucide-react";
import { showError } from "@/utils/dialog";
import {
  startOfYearLocalDateInputValue,
  toLocalDateInputValue,
} from "@/utils/date";

export default function Reports() {
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const parsedReportPermissions =
    typeof user?.report_permissions === "string"
      ? (() => {
          try {
            return JSON.parse(user.report_permissions);
          } catch (_) {
            return null;
          }
        })()
      : user?.report_permissions || null;
  const allowedReportKeys = useMemo(
    () =>
      Array.isArray(parsedReportPermissions?.allowed_reports)
        ? parsedReportPermissions.allowed_reports
        : [],
    [parsedReportPermissions],
  );
  const canAccessReportKey = useCallback(
    (key) =>
      role === "admin" ||
      (parsedReportPermissions?.enabled === true && allowedReportKeys.includes(key)),
    [role, parsedReportPermissions?.enabled, allowedReportKeys],
  );
  const canLoadStaffOptions = role === "admin" || (parsedReportPermissions?.enabled === true && allowedReportKeys.includes("staff_options"));

  const [activeTab, setActiveTab] = useState("profit-loss");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const todayDate = toLocalDateInputValue();
  const startOfYearDate = startOfYearLocalDateInputValue();

  // Profit & Loss
  const [profitLossData, setProfitLossData] = useState(null);
  const [plDateRange, setPlDateRange] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
  });
  const [plFilters, setPlFilters] = useState({
    customer_id: "",
    vendor_id: "",
    staff_id: "",
  });

  // Collections
  const [collectionsData, setCollectionsData] = useState(null);

  // Income Status
  const [incomeData, setIncomeData] = useState([]);
  const [incomeDateRange, setIncomeDateRange] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
  });

  // Expense Report
  const [expenseData, setExpenseData] = useState([]);
  const [expenseDateRange, setExpenseDateRange] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
  });

  // Service profitability
  const [serviceProfitData, setServiceProfitData] = useState([]);
  const [serviceProfitDateRange, setServiceProfitDateRange] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
  });
  const [serviceProfitSalesperson, setServiceProfitSalesperson] = useState("");

  // Financial reports (GAP 2.2)
  const [financialSubTab, setFinancialSubTab] = useState("revenue-by-period");
  const [financialDateRange, setFinancialDateRange] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
  });
  const [financialGroupBy, setFinancialGroupBy] = useState("month");
  const [revenueByPeriodData, setRevenueByPeriodData] = useState([]);
  const [revenueBySalespersonData, setRevenueBySalespersonData] = useState([]);
  const [revenueByClientData, setRevenueByClientData] = useState([]);
  const [revenueByServiceTypeData, setRevenueByServiceTypeData] = useState([]);
  const [outstandingAgingData, setOutstandingAgingData] = useState(null);
  const [paidInvoicesData, setPaidInvoicesData] = useState(null);
  const [expenseByCategoryData, setExpenseByCategoryData] = useState([]);
  const [plStatementData, setPlStatementData] = useState(null);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState(null);
  const [dailyOperationsData, setDailyOperationsData] = useState(null);

  // ROI analytics (GAP 2.3)
  const [roiData, setRoiData] = useState(null);
  const [roiDateRange, setRoiDateRange] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
  });
  const [roiServiceType, setRoiServiceType] = useState("");
  const [roiSalesperson, setRoiSalesperson] = useState("");

  // PAX reports
  const [paxByServiceData, setPaxByServiceData] = useState([]);
  const [revenuePerPaxData, setRevenuePerPaxData] = useState(null);
  const [paxTrendsData, setPaxTrendsData] = useState([]);
  const [paxDateRange, setPaxDateRange] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
  });
  const [paxTrendsGroupBy, setPaxTrendsGroupBy] = useState("month");
  const [reportCurrency, setReportCurrency] = useState("OMR");
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [plCustomerQuery, setPlCustomerQuery] = useState("");
  const [plVendorQuery, setPlVendorQuery] = useState("");
  const [ledgerCustomerQuery, setLedgerCustomerQuery] = useState("");
  const [ledgerVendorQuery, setLedgerVendorQuery] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [ledgerFilters, setLedgerFilters] = useState({
    start_date: startOfYearDate,
    end_date: todayDate,
    customer_id: "",
    vendor_id: "",
    staff_id: "",
  });
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [statementFilters, setStatementFilters] = useState({
    party_type: "customer",
    party_id: "",
  });
  const [statementData, setStatementData] = useState(null);
  const [statementViewMode, setStatementViewMode] = useState("detailed");

  useEffect(() => {
    api.settings.getCompany().then((s) => setReportCurrency(s?.currency || "OMR")).catch(() => {});
    api.customers.getAll().then(setCustomers).catch(() => setCustomers([]));
    api.vendors.getAll().then(setVendors).catch(() => setVendors([]));
    if (canLoadStaffOptions) {
      api.reports.staffOptions().then(setStaffOptions).catch(() => setStaffOptions([]));
    } else {
      setStaffOptions([]);
    }
  }, [canLoadStaffOptions]);

  const customerOptionLabel = (customer) => customer?.name || "";
  const vendorOptionLabel = (vendor) => vendor?.name || "";

  useEffect(() => {
    if (activeTab === "collections") {
      fetchCollections();
    }
  }, [activeTab]);

  const fetchProfitLoss = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.profitLoss({
        start_date: plDateRange.start_date,
        end_date: plDateRange.end_date,
        ...plFilters,
      });
      setProfitLossData(data);
    } catch (error) {
      console.error("Failed to fetch profit & loss:", error);
      setError(error.message || "Failed to fetch Profit & Loss report");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.collections();
      setCollectionsData(data);
    } catch (error) {
      console.error("Failed to fetch collections:", error);
      setError(error.message || "Failed to fetch Collections report");
    } finally {
      setLoading(false);
    }
  };

  const fetchIncomeStatus = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.income.getAll({
        start_date: incomeDateRange.start_date,
        end_date: incomeDateRange.end_date,
      });
      setIncomeData(data);
    } catch (error) {
      console.error("Failed to fetch income:", error);
      setError(error.message || "Failed to fetch Income Status report");
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseReport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.expenses.getAll({
        start_date: expenseDateRange.start_date,
        end_date: expenseDateRange.end_date,
      });
      setExpenseData(data);
    } catch (error) {
      console.error("Failed to fetch expenses:", error);
      setError(error.message || "Failed to fetch Expense report");
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceProfitability = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.serviceProfitability(
        serviceProfitDateRange.start_date,
        serviceProfitDateRange.end_date,
        serviceProfitSalesperson || undefined
      );
      setServiceProfitData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch service profitability:", error);
      setError(error.message || "Failed to fetch Service Profitability report");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async (reportType) => {
    try {
      let startDate = null;
      let endDate = null;

      if (reportType === "profit-loss") {
        startDate = plDateRange.start_date;
        endDate = plDateRange.end_date;
      } else if (reportType === "income") {
        startDate = incomeDateRange.start_date;
        endDate = incomeDateRange.end_date;
      } else if (reportType === "expenses") {
        startDate = expenseDateRange.start_date;
        endDate = expenseDateRange.end_date;
      }

      const response = await api.reports.exportPDF(
        reportType,
        startDate,
        endDate
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${reportType}-report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export PDF:", error);
      await showError("Failed to export PDF. Please try again.");
    }
  };

  const handleExportExcel = async (reportType) => {
    try {
      let startDate = null;
      let endDate = null;

      if (reportType === "profit-loss") {
        startDate = plDateRange.start_date;
        endDate = plDateRange.end_date;
      } else if (reportType === "income") {
        startDate = incomeDateRange.start_date;
        endDate = incomeDateRange.end_date;
      } else if (reportType === "expenses") {
        startDate = expenseDateRange.start_date;
        endDate = expenseDateRange.end_date;
      }

      const response = await api.reports.exportExcel(
        reportType,
        startDate,
        endDate
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${reportType}-report.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export Excel:", error);
      await showError("Failed to export Excel. Please try again.");
    }
  };

  const fetchFinancialReport = async () => {
    setLoading(true);
    setError("");
    try {
      const { start_date, end_date } = financialDateRange;
      if (financialSubTab === "revenue-by-period") {
        const data = await api.reports.revenueByPeriod(financialGroupBy, start_date, end_date);
        setRevenueByPeriodData(Array.isArray(data) ? data : []);
      } else if (financialSubTab === "revenue-by-salesperson") {
        const data = await api.reports.revenueBySalesperson(start_date, end_date);
        setRevenueBySalespersonData(Array.isArray(data) ? data : []);
      } else if (financialSubTab === "revenue-by-client") {
        const data = await api.reports.revenueByClient(start_date, end_date);
        setRevenueByClientData(Array.isArray(data) ? data : []);
      } else if (financialSubTab === "revenue-by-service-type") {
        const data = await api.reports.revenueByServiceType(start_date, end_date);
        setRevenueByServiceTypeData(Array.isArray(data) ? data : []);
      } else if (financialSubTab === "outstanding-aging") {
        const data = await api.reports.outstandingAging();
        setOutstandingAgingData(data);
      } else if (financialSubTab === "paid-invoices") {
        const data = await api.reports.paidInvoices(start_date, end_date);
        setPaidInvoicesData(data);
      } else if (financialSubTab === "expense-by-category") {
        const data = await api.reports.expenseByCategory(start_date, end_date);
        setExpenseByCategoryData(Array.isArray(data) ? data : []);
      } else if (financialSubTab === "pl-statement") {
        const data = await api.reports.plStatement(start_date, end_date);
        setPlStatementData(data);
      } else if (financialSubTab === "balance-sheet") {
        const data = await api.accounting.getBalanceSheet(end_date || start_date);
        setBalanceSheetData(data);
      } else if (financialSubTab === "cash-flow") {
        const data = await api.accounting.getCashFlow(start_date, end_date);
        setCashFlowData(data);
      } else if (financialSubTab === "daily-operations") {
        const data = await api.reports.dailyOperations(end_date || start_date);
        setDailyOperationsData(data);
      }
    } catch (err) {
      console.error("Failed to fetch financial report:", err);
      setError(err.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoi = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.roi(
        roiDateRange.start_date,
        roiDateRange.end_date,
        roiServiceType || undefined,
        roiSalesperson || undefined
      );
      setRoiData(data);
    } catch (err) {
      console.error("Failed to fetch ROI:", err);
      setError(err.message || "Failed to fetch ROI analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaxByService = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.paxByService(paxDateRange.start_date, paxDateRange.end_date);
      setPaxByServiceData(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to fetch PAX by service");
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenuePerPax = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.revenuePerPax(paxDateRange.start_date, paxDateRange.end_date);
      setRevenuePerPaxData(data);
    } catch (err) {
      setError(err.message || "Failed to fetch revenue per PAX");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaxTrends = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.paxTrends(paxTrendsGroupBy, paxDateRange.start_date, paxDateRange.end_date);
      setPaxTrendsData(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to fetch PAX trends");
    } finally {
      setLoading(false);
    }
  };

  const fetchLedgerSummary = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.reports.ledgerSummary(ledgerFilters);
      setLedgerSummary(data);
    } catch (err) {
      setError(err.message || "Failed to fetch ledger summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchStatement = async () => {
    if (!statementFilters.party_id) {
      setError("Select a customer or vendor");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const statementApi =
        statementViewMode === "signed"
          ? api.reports.statementSigned
          : api.reports.statement;
      const data = await statementApi(
        statementFilters.party_type,
        statementFilters.party_id,
        ledgerFilters.start_date,
        ledgerFilters.end_date,
      );
      setStatementData(data);
    } catch (err) {
      setError(err.message || "Failed to fetch statement");
    } finally {
      setLoading(false);
    }
  };

  const financialSubTabs = useMemo(
    () =>
      [
        {
          id: "revenue-by-period",
          label: "Revenue by Period",
          permissionKey: "revenue_by_period",
        },
        {
          id: "revenue-by-salesperson",
          label: "Revenue by Salesperson",
          permissionKey: "revenue_by_salesperson",
        },
        {
          id: "revenue-by-client",
          label: "Revenue by Client",
          permissionKey: "revenue_by_client",
        },
        {
          id: "revenue-by-service-type",
          label: "Revenue by Service Type",
          permissionKey: "revenue_by_service_type",
        },
        {
          id: "outstanding-aging",
          label: "Outstanding (Aging)",
          permissionKey: "outstanding_aging",
        },
        { id: "paid-invoices", label: "Paid Invoices", permissionKey: "paid_invoices" },
        {
          id: "expense-by-category",
          label: "Expense by Category",
          permissionKey: "expense_by_category",
        },
        { id: "pl-statement", label: "P&L Statement", permissionKey: "pl_statement" },
        { id: "balance-sheet", label: "Balance Sheet", permissionKey: "pl_statement" },
        { id: "cash-flow", label: "Cash Flow", permissionKey: "pl_statement" },
        {
          id: "daily-operations",
          label: "Daily Operations",
          permissionKey: "daily_operations",
        },
      ].filter((tab) => canAccessReportKey(tab.permissionKey)),
    [canAccessReportKey],
  );

  const tabs = useMemo(
    () =>
      [
        canAccessReportKey("profit_loss") && {
          id: "profit-loss",
          label: "Profit & Loss",
          icon: TrendingUp,
        },
        canAccessReportKey("collections") && {
          id: "collections",
          label: "Collection Status",
          icon: DollarSign,
        },
        financialSubTabs.length > 0 && { id: "financial", label: "Financial", icon: DollarSign },
        canAccessReportKey("roi") && { id: "roi", label: "ROI Analytics", icon: BarChart3 },
        (canAccessReportKey("pax_by_service") ||
          canAccessReportKey("revenue_per_pax") ||
          canAccessReportKey("pax_trends")) && {
          id: "pax",
          label: "PAX Reports",
          icon: Users,
        },
        (canAccessReportKey("ledger_summary") || canAccessReportKey("statement")) && {
          id: "ledgers",
          label: "Statements & Ledgers",
          icon: FileText,
        },
        canAccessReportKey("service_profitability") && {
          id: "service-profitability",
          label: "Service Profitability",
          icon: BarChart3,
        },
      ].filter(Boolean),
    [canAccessReportKey, financialSubTabs.length],
  );

  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  useEffect(() => {
    if (!financialSubTabs.length) return;
    if (!financialSubTabs.some((tab) => tab.id === financialSubTab)) {
      setFinancialSubTab(financialSubTabs[0].id);
    }
  }, [financialSubTabs, financialSubTab]);

  const totalIncome = incomeData.reduce(
    (sum, item) => sum + parseFloat(item.amount || 0),
    0
  );

  const totalExpenses = expenseData.reduce(
    (sum, item) => sum + parseFloat(item.amount || 0),
    0
  );

  return (
    <div className="space-y-6">
      {!tabs.length && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
          No reports are assigned to your account yet. Ask admin to enable specific reports in Team Management.
        </div>
      )}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-heading font-semibold text-secondary">
          Reports
        </h1>
      </div>

      {/* Tabs: scrollable so all tabs visible without overflow */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
        <div className="border-b border-stone-200 overflow-x-auto">
          <div className="flex min-w-max flex-nowrap gap-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`shrink-0 flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-semibold transition-colors whitespace-nowrap min-w-[11rem] ${
                    activeTab === tab.id
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                  }`}
                >
                  <Icon size={18} className="shrink-0" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Profit & Loss Report */}
          {activeTab === "profit-loss" && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={plDateRange.start_date}
                      onChange={(e) =>
                        setPlDateRange({
                          ...plDateRange,
                          start_date: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={plDateRange.end_date}
                      onChange={(e) =>
                        setPlDateRange({
                          ...plDateRange,
                          end_date: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Customer
                    </label>
                    <input
                      type="text"
                      value={plCustomerQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPlCustomerQuery(val);
                        const selectedCustomer = customers.find(
                          (c) => customerOptionLabel(c) === val,
                        );
                        setPlFilters((prev) => ({
                          ...prev,
                          customer_id: selectedCustomer?.id || "",
                          vendor_id: selectedCustomer ? "" : prev.vendor_id,
                        }));
                        if (selectedCustomer) setPlVendorQuery("");
                      }}
                      list="pl-customer-options"
                      placeholder="All customers (type to search)"
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                    <datalist id="pl-customer-options">
                      {customers.map((customer) => (
                        <option key={customer.id} value={customerOptionLabel(customer)} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Vendor
                    </label>
                    <input
                      type="text"
                      value={plVendorQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPlVendorQuery(val);
                        const selectedVendor = vendors.find(
                          (v) => vendorOptionLabel(v) === val,
                        );
                        setPlFilters((prev) => ({
                          ...prev,
                          vendor_id: selectedVendor?.id || "",
                          customer_id: selectedVendor ? "" : prev.customer_id,
                        }));
                        if (selectedVendor) setPlCustomerQuery("");
                      }}
                      list="pl-vendor-options"
                      placeholder="All vendors (type to search)"
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                    <datalist id="pl-vendor-options">
                      {vendors.map((vendor) => (
                        <option key={vendor.id} value={vendorOptionLabel(vendor)} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Staff
                    </label>
                    <select
                      value={plFilters.staff_id}
                      onChange={(e) =>
                        setPlFilters((prev) => ({
                          ...prev,
                          staff_id: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    >
                      <option value="">All staff</option>
                      {staffOptions.map((staff) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.name} ({staff.role})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={fetchProfitLoss}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Generate Report"}
                </button>
              </div>

              {profitLossData && (
                <div className="space-y-4">
                  <div className="flex gap-4 justify-end">
                    <button
                      onClick={() => handleExportPDF("profit-loss")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileText size={18} />
                      Export PDF
                    </button>
                    <button
                      onClick={() => handleExportExcel("profit-loss")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileSpreadsheet size={18} />
                      Export Excel
                    </button>
                  </div>

                  <div className="bg-stone-50 rounded-lg p-6 space-y-4">
                    <div className="flex justify-between items-center pb-4 border-b border-stone-200">
                      <span className="text-stone-600">Total Income:</span>
                      <span className="text-xl font-semibold text-emerald-600">
                        {reportCurrency} {profitLossData.total_income.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pb-4 border-b border-stone-200">
                      <span className="text-stone-600">Total Expenses:</span>
                      <span className="text-xl font-semibold text-red-600">
                        {reportCurrency} {profitLossData.total_expenses.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-lg font-semibold text-secondary">
                        Net Profit/Loss:
                      </span>
                      <span
                        className={`text-2xl font-bold ${
                          profitLossData.profit >= 0
                            ? "text-emerald-600"
                            : "text-red-600"
                        }`}
                      >
                        {reportCurrency} {profitLossData.profit.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-stone-300 overflow-hidden">
                    <div className="px-4 py-3 border-b border-stone-300 bg-stone-100">
                      <h3 className="font-semibold text-stone-900 text-center text-lg">
                        Profit and Loss Statement (Detailed)
                      </h3>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                          <th className="px-4 py-2 text-left font-semibold text-stone-700">Description</th>
                          <th className="px-4 py-2 text-right font-semibold text-stone-700">Amount ({reportCurrency})</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        <tr className="bg-emerald-50">
                          <td className="px-4 py-2 font-semibold text-stone-800">Income</td>
                          <td />
                        </tr>
                        {(profitLossData.income_items || []).length === 0 ? (
                          <tr>
                            <td className="px-4 py-2 text-stone-500">No income lines in selected filters</td>
                            <td className="px-4 py-2 text-right text-stone-500">0.00</td>
                          </tr>
                        ) : (
                          (profitLossData.income_items || []).map((row, idx) => (
                            <tr key={`inc-${row.invoice_id || idx}-${idx}`}>
                              <td className="px-4 py-2 text-stone-700">
                                {row.description || "Income"} {row.customer_name ? `- ${row.customer_name}` : ""}
                              </td>
                              <td className="px-4 py-2 text-right font-mono text-stone-900">
                                {Number(row.amount || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        )}
                        <tr className="bg-emerald-100">
                          <td className="px-4 py-2 font-bold text-stone-900">Total Income</td>
                          <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                            {Number(profitLossData.total_income || 0).toFixed(2)}
                          </td>
                        </tr>

                        <tr className="bg-rose-50">
                          <td className="px-4 py-2 font-semibold text-stone-800">Expenses</td>
                          <td />
                        </tr>
                        {(profitLossData.expense_items || []).length === 0 ? (
                          <tr>
                            <td className="px-4 py-2 text-stone-500">No expense lines in selected filters</td>
                            <td className="px-4 py-2 text-right text-stone-500">0.00</td>
                          </tr>
                        ) : (
                          (profitLossData.expense_items || []).map((row, idx) => (
                            <tr key={`exp-${row.id || idx}-${idx}`}>
                              <td className="px-4 py-2 text-stone-700">
                                {row.description || row.category || "Expense"}
                              </td>
                              <td className="px-4 py-2 text-right font-mono text-stone-900">
                                {Number(row.amount || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))
                        )}
                        <tr className="bg-rose-100">
                          <td className="px-4 py-2 font-bold text-stone-900">Total Expenses</td>
                          <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                            {Number(profitLossData.total_expenses || 0).toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-amber-100 border-t-2 border-stone-400">
                          <td className="px-4 py-2 font-bold text-stone-900">Net Profit / Loss</td>
                          <td
                            className={`px-4 py-2 text-right font-mono font-bold ${
                              Number(profitLossData.profit || 0) >= 0 ? "text-emerald-700" : "text-red-700"
                            }`}
                          >
                            {Number(profitLossData.profit || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Collection Status Report */}
          {activeTab === "collections" && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <button
                  onClick={fetchCollections}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Refresh Report"}
                </button>
              </div>

              {collectionsData && (
                <div className="space-y-4">
                  <div className="flex gap-4 justify-end">
                    <button
                      onClick={() => handleExportPDF("collections")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileText size={18} />
                      Export PDF
                    </button>
                    <button
                      onClick={() => handleExportExcel("collections")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileSpreadsheet size={18} />
                      Export Excel
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-stone-50 rounded-lg p-4">
                      <p className="text-sm text-stone-600 mb-1">
                        Total Invoiced
                      </p>
                      <p className="text-xl font-semibold text-secondary">
                        {reportCurrency} {collectionsData.total_invoiced.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <p className="text-sm text-stone-600 mb-1">
                        Total Collected
                      </p>
                      <p className="text-xl font-semibold text-emerald-600">
                        {reportCurrency} {collectionsData.total_collected.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4">
                      <p className="text-sm text-stone-600 mb-1">
                        Pending Collection
                      </p>
                      <p className="text-xl font-semibold text-amber-600">
                        {reportCurrency} {collectionsData.pending_collection.toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-stone-50 rounded-lg p-4">
                      <p className="text-sm text-stone-600 mb-1">
                        Paid Invoices
                      </p>
                      <p className="text-xl font-semibold text-secondary">
                        {collectionsData.paid_count}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Invoice #
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Customer
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">
                              Total
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">
                              Paid
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">
                              Pending
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {collectionsData.unpaid_invoices.length === 0 &&
                          collectionsData.partial_invoices.length === 0 ? (
                            <tr>
                              <td
                                colSpan={6}
                                className="px-4 py-8 text-center text-stone-500"
                              >
                                No pending collections
                              </td>
                            </tr>
                          ) : (
                            <>
                              {collectionsData.unpaid_invoices.map(
                                (invoice) => (
                                  <tr
                                    key={invoice.id}
                                    className="hover:bg-stone-50"
                                  >
                                    <td className="px-4 py-3 font-mono text-sm">
                                      {invoice.invoice_number}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {invoice.customer_name}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-sm">
                                      {invoice.currency}{" "}
                                      {parseFloat(invoice.total).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-sm text-stone-500">
                                      {invoice.currency}{" "}
                                      {parseFloat(
                                        invoice.amount_paid || 0
                                      ).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-red-600">
                                      {invoice.currency}{" "}
                                      {(
                                        parseFloat(invoice.total) -
                                        parseFloat(invoice.amount_paid || 0)
                                      ).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                                        Unpaid
                                      </span>
                                    </td>
                                  </tr>
                                )
                              )}
                              {collectionsData.partial_invoices.map(
                                (invoice) => (
                                  <tr
                                    key={invoice.id}
                                    className="hover:bg-stone-50"
                                  >
                                    <td className="px-4 py-3 font-mono text-sm">
                                      {invoice.invoice_number}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {invoice.customer_name}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-sm">
                                      {invoice.currency}{" "}
                                      {parseFloat(invoice.total).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-sm text-emerald-600">
                                      {invoice.currency}{" "}
                                      {parseFloat(
                                        invoice.amount_paid || 0
                                      ).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-amber-600">
                                      {invoice.currency}{" "}
                                      {(
                                        parseFloat(invoice.total) -
                                        parseFloat(invoice.amount_paid || 0)
                                      ).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3">
                                      <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                                        Partial
                                      </span>
                                    </td>
                                  </tr>
                                )
                              )}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sales Funnel Report */}
          {activeTab === "ledgers" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={ledgerFilters.start_date}
                    onChange={(e) => setLedgerFilters((p) => ({ ...p, start_date: e.target.value }))}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={ledgerFilters.end_date}
                    onChange={(e) => setLedgerFilters((p) => ({ ...p, end_date: e.target.value }))}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Customer Filter</label>
                  <input
                    type="text"
                    value={ledgerCustomerQuery}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLedgerCustomerQuery(val);
                      const selectedCustomer = customers.find(
                        (c) => customerOptionLabel(c) === val,
                      );
                      setLedgerFilters((p) => ({
                        ...p,
                        customer_id: selectedCustomer?.id || "",
                        vendor_id: selectedCustomer ? "" : p.vendor_id,
                      }));
                      if (selectedCustomer) setLedgerVendorQuery("");
                    }}
                    list="ledger-customer-options"
                    placeholder="All customers (type to search)"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                  <datalist id="ledger-customer-options">
                    {customers.map((customer) => (
                      <option key={customer.id} value={customerOptionLabel(customer)} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Vendor Filter</label>
                  <input
                    type="text"
                    value={ledgerVendorQuery}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLedgerVendorQuery(val);
                      const selectedVendor = vendors.find(
                        (v) => vendorOptionLabel(v) === val,
                      );
                      setLedgerFilters((p) => ({
                        ...p,
                        vendor_id: selectedVendor?.id || "",
                        customer_id: selectedVendor ? "" : p.customer_id,
                      }));
                      if (selectedVendor) setLedgerCustomerQuery("");
                    }}
                    list="ledger-vendor-options"
                    placeholder="All vendors (type to search)"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                  />
                  <datalist id="ledger-vendor-options">
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendorOptionLabel(vendor)} />
                    ))}
                  </datalist>
                </div>
                <button
                  onClick={fetchLedgerSummary}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Ledger Summary"}
                </button>
              </div>

              {ledgerSummary && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {[
                    ["Income", ledgerSummary.income, "text-emerald-600"],
                    ["Expense", ledgerSummary.expense, "text-red-600"],
                    ["Amount to Receive", ledgerSummary.receivable, "text-amber-600"],
                    ["Amount to Pay", ledgerSummary.payable, "text-orange-600"],
                    ["Company Balance", ledgerSummary.cash, "text-cyan-600"],
                  ].map(([label, value, color]) => (
                    <div key={label} className="bg-stone-50 rounded-lg p-4">
                      <p className="text-sm text-stone-600 mb-1">{label}</p>
                      <p className={`text-xl font-semibold ${color}`}>
                        {reportCurrency} {Number(value || 0).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-4">
                <h3 className="font-semibold text-secondary">Customer / Vendor Statement</h3>
                <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-4 items-end">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Statement Type</label>
                    <select
                      value={statementFilters.party_type}
                      onChange={(e) => setStatementFilters({ party_type: e.target.value, party_id: "" })}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    >
                      <option value="customer">Customer</option>
                      <option value="vendor">Vendor</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      {statementFilters.party_type === "customer" ? "Customer" : "Vendor"}
                    </label>
                    <select
                      value={statementFilters.party_id}
                      onChange={(e) => setStatementFilters((p) => ({ ...p, party_id: e.target.value }))}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    >
                      <option value="">Select</option>
                      {(statementFilters.party_type === "customer" ? customers : vendors).map((item) => (
                        <option key={item.id} value={item.id}>{item.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">View Mode</label>
                    <select
                      value={statementViewMode}
                      onChange={(e) => setStatementViewMode(e.target.value)}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg"
                    >
                      <option value="detailed">Debit / Credit + Running Balance</option>
                      <option value="signed">Signed Amount + Running Balance</option>
                    </select>
                  </div>
                  <button
                    onClick={fetchStatement}
                    disabled={loading}
                    className="px-6 py-2 bg-secondary hover:bg-secondary/90 text-white font-medium rounded-lg disabled:opacity-50"
                  >
                    Generate Statement
                  </button>
                </div>
              </div>

              {statementData && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-stone-50 border-b border-stone-200">
                    <p className="font-medium">
                      Opening Balance: {reportCurrency} {Number(statementData.opening_balance || 0).toFixed(2)}
                    </p>
                    <p className="font-medium text-right">
                      Closing Balance: {reportCurrency} {Number(statementData.closing_balance || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="px-4 py-3 text-left">Date</th>
                          <th className="px-4 py-3 text-left">Type</th>
                          <th className="px-4 py-3 text-left">Description</th>
                          {statementViewMode === "signed" ? (
                            <>
                              <th className="px-4 py-3 text-right">Signed Amount</th>
                              <th className="px-4 py-3 text-right">Running Balance</th>
                            </>
                          ) : (
                            <>
                              <th className="px-4 py-3 text-right">Debit</th>
                              <th className="px-4 py-3 text-right">Credit</th>
                              <th className="px-4 py-3 text-right">Running Balance</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {(statementData.transactions || []).map((row) => (
                          <tr key={row.id}>
                            <td className="px-4 py-3">{new Date(row.entry_date).toLocaleDateString()}</td>
                            <td className="px-4 py-3">{row.entry_type}</td>
                            <td className="px-4 py-3">{row.description || "-"}</td>
                            {statementViewMode === "signed" ? (
                              <>
                                <td className="px-4 py-3 text-right font-mono">
                                  {row.currency || reportCurrency} {Number(row.signed_amount || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono">
                                  {row.currency || reportCurrency} {Number(row.running_balance || 0).toFixed(2)}
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="px-4 py-3 text-right font-mono">
                                  {row.currency || reportCurrency} {Number(row.debit || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono">
                                  {row.currency || reportCurrency} {Number(row.credit || 0).toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono">
                                  {row.currency || reportCurrency} {Number(row.running_balance || 0).toFixed(2)}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Financial Reports (GAP 2.2) */}
          {activeTab === "financial" && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2 border-b border-stone-200 pb-4">
                {financialSubTabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setFinancialSubTab(t.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      financialSubTab === t.id
                        ? "bg-primary text-white"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 items-end">
                {financialSubTab !== "outstanding-aging" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={financialDateRange.start_date}
                        onChange={(e) => setFinancialDateRange({ ...financialDateRange, start_date: e.target.value })}
                        className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={financialDateRange.end_date}
                        onChange={(e) => setFinancialDateRange({ ...financialDateRange, end_date: e.target.value })}
                        className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      />
                    </div>
                  </>
                )}
                {financialSubTab === "revenue-by-period" && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Group by</label>
                    <select
                      value={financialGroupBy}
                      onChange={(e) => setFinancialGroupBy(e.target.value)}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    >
                      <option value="day">Day</option>
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                )}
                <button
                  onClick={fetchFinancialReport}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Generate Report"}
                </button>
              </div>

              {financialSubTab === "revenue-by-period" && revenueByPeriodData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Period</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue ({reportCurrency})</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Invoices</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {revenueByPeriodData.map((r) => (
                        <tr key={r.period} className="hover:bg-stone-50">
                          <td className="px-4 py-3 font-medium text-stone-800">{r.period}</td>
                          <td className="px-4 py-3 text-right font-mono">{r.total_revenue?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">{r.invoice_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {financialSubTab === "revenue-by-salesperson" && revenueBySalespersonData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Salesperson</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue ({reportCurrency})</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Invoices</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {revenueBySalespersonData.map((r) => (
                        <tr key={r.user_id} className="hover:bg-stone-50">
                          <td className="px-4 py-3 font-medium text-stone-800">{r.salesperson_name}</td>
                          <td className="px-4 py-3 text-right font-mono">{r.total_revenue?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">{r.invoice_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {financialSubTab === "revenue-by-client" && revenueByClientData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Client</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue ({reportCurrency})</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Invoices</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {revenueByClientData.map((r) => (
                        <tr key={r.customer_id} className="hover:bg-stone-50">
                          <td className="px-4 py-3 font-medium text-stone-800">{r.customer_name}</td>
                          <td className="px-4 py-3 text-right font-mono">{r.total_revenue?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">{r.invoice_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {financialSubTab === "revenue-by-service-type" && revenueByServiceTypeData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Service Type</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue ({reportCurrency})</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {revenueByServiceTypeData.map((r) => (
                        <tr key={r.service_type} className="hover:bg-stone-50">
                          <td className="px-4 py-3 font-medium text-stone-800">{r.service_type?.replace(/_/g, " ")}</td>
                          <td className="px-4 py-3 text-right font-mono">{r.total_revenue?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {financialSubTab === "outstanding-aging" && outstandingAgingData && (
                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="text-sm text-stone-600">Total Outstanding</p>
                    <p className="text-2xl font-bold text-amber-700">{reportCurrency} {outstandingAgingData.total_outstanding?.toFixed(2)}</p>
                  </div>
                  {["0-30", "31-60", "61-90", "90+"].map((bucket) => (
                    <div key={bucket} className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                      <h3 className="text-lg font-semibold text-stone-800 p-4 border-b border-stone-200">
                        {bucket} days {bucket === "0-30" ? "(current)" : "overdue"} — {reportCurrency} {(outstandingAgingData.bucket_totals || {})[bucket]?.toFixed(2) ?? "0.00"}
                      </h3>
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Invoice</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Customer</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Pending</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Days Overdue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {((outstandingAgingData.buckets || {})[bucket] || []).map((inv) => (
                            <tr key={inv.id} className="hover:bg-stone-50">
                              <td className="px-4 py-3 font-medium text-stone-800">{inv.invoice_number}</td>
                              <td className="px-4 py-3 text-stone-600">{inv.customer_name}</td>
                              <td className="px-4 py-3 text-right font-mono">{reportCurrency} {inv.pending?.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right">{inv.days_overdue}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
              {financialSubTab === "paid-invoices" && paidInvoicesData && (
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <p className="text-sm text-stone-600">Total Collected</p>
                    <p className="text-2xl font-bold text-emerald-700">{reportCurrency} {paidInvoicesData.total_collected?.toFixed(2)} ({paidInvoicesData.count} invoices)</p>
                  </div>
                  <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Invoice</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Customer</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Total</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Paid</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {(paidInvoicesData.invoices || []).map((inv) => (
                          <tr key={inv.id} className="hover:bg-stone-50">
                            <td className="px-4 py-3 font-medium text-stone-800">{inv.invoice_number}</td>
                            <td className="px-4 py-3 text-stone-600">{inv.customer_name}</td>
                            <td className="px-4 py-3 text-right font-mono">{reportCurrency} {parseFloat(inv.total).toFixed(2)}</td>
                            <td className="px-4 py-3 text-right font-mono">{reportCurrency} {parseFloat(inv.amount_paid || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {financialSubTab === "expense-by-category" && expenseByCategoryData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Total ({reportCurrency})</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {expenseByCategoryData.map((r) => (
                        <tr key={r.category} className="hover:bg-stone-50">
                          <td className="px-4 py-3 font-medium text-stone-800">{r.category}</td>
                          <td className="px-4 py-3 text-right font-mono">{r.total?.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {financialSubTab === "pl-statement" && plStatementData && (
                <div className="bg-white rounded-lg border border-stone-300 overflow-hidden max-w-5xl">
                  <div className="px-4 py-3 border-b border-stone-300 bg-stone-100">
                    <h3 className="font-semibold text-stone-900 text-center text-xl">
                      Profit and Loss Statement
                    </h3>
                  </div>

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-300">
                        <th className="px-4 py-2 text-left font-semibold text-stone-800 bg-stone-50 w-[55%]">
                          Income
                        </th>
                        <th className="px-4 py-2 text-right font-semibold text-stone-800 bg-amber-100 w-[25%]">
                          Partial
                        </th>
                        <th className="px-4 py-2 text-right font-semibold text-stone-800 bg-amber-100 w-[20%]">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      <tr>
                        <td className="px-4 py-1.5 font-semibold text-stone-800 bg-yellow-100">
                          Operating Income
                        </td>
                        <td className="px-4 py-1.5" />
                        <td className="px-4 py-1.5 text-right font-mono font-semibold text-stone-900">
                          {Number(plStatementData.total_revenue || 0).toFixed(2)}
                        </td>
                      </tr>
                      {(plStatementData.operating_income_breakdown || []).map((row) => (
                        <tr key={`inc-${row.name}`}>
                          <td className="px-4 py-1.5 text-stone-700">{row.name}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-stone-900">
                            {Number(row.amount || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-1.5" />
                        </tr>
                      ))}

                      <tr>
                        <td className="px-4 py-1.5 font-semibold text-stone-800 bg-yellow-100">
                          Cost of Revenue
                        </td>
                        <td className="px-4 py-1.5" />
                        <td className="px-4 py-1.5 text-right font-mono font-semibold text-stone-900">
                          {Number(plStatementData.total_cost || 0).toFixed(2)}
                        </td>
                      </tr>
                      {(plStatementData.cost_of_revenue_breakdown || []).map((row) => (
                        <tr key={`cost-${row.name}`}>
                          <td className="px-4 py-1.5 text-stone-700">{row.name}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-stone-900">
                            {Number(row.amount || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-1.5" />
                        </tr>
                      ))}

                      <tr className="border-t border-stone-300">
                        <td className="px-4 py-2 font-bold text-stone-800 bg-sky-100">
                          Total Income
                        </td>
                        <td className="px-4 py-2" />
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900 bg-sky-200">
                          {Number(plStatementData.gross_profit || 0).toFixed(2)}
                        </td>
                      </tr>

                      <tr>
                        <td className="px-4 py-2 font-semibold text-stone-800 bg-stone-50">
                          Expenses
                        </td>
                        <td className="px-4 py-2" />
                        <td className="px-4 py-2" />
                      </tr>
                      {(plStatementData.operating_expenses_breakdown || []).map((row) => (
                        <tr key={`exp-${row.name}`}>
                          <td className="px-4 py-1.5 text-stone-700">{row.name}</td>
                          <td className="px-4 py-1.5 text-right font-mono text-stone-900">
                            {Number(row.amount || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-1.5" />
                        </tr>
                      ))}

                      <tr className="border-t border-stone-300">
                        <td className="px-4 py-2 font-bold text-stone-800 bg-sky-100">
                          Total Expenses
                        </td>
                        <td className="px-4 py-2" />
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900 bg-sky-200">
                          {Number(
                            plStatementData.total_operating_expenses ||
                              plStatementData.total_expenses ||
                              0,
                          ).toFixed(2)}
                        </td>
                      </tr>

                      <tr className="border-t-2 border-stone-400">
                        <td className="px-4 py-2.5 font-bold text-stone-900 bg-amber-100">
                          Net Profit
                        </td>
                        <td className="px-4 py-2.5 bg-amber-100" />
                        <td className="px-4 py-2.5 text-right font-mono font-bold text-stone-900 bg-amber-200">
                          {Number(plStatementData.net_profit || 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {financialSubTab === "balance-sheet" && balanceSheetData && (
                <div className="bg-white rounded-lg border border-stone-300 overflow-hidden">
                  <div className="px-4 py-3 border-b border-stone-300 bg-stone-100">
                    <h3 className="font-semibold text-stone-900 text-center text-xl">
                      Statement of Financial Position
                    </h3>
                  </div>
                  <div className="px-4 py-2 border-b border-stone-200 flex justify-end text-sm font-semibold text-stone-700">
                    As of {balanceSheetData.as_of_date || financialDateRange.end_date}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-stone-50 border-b border-stone-200">
                        <th className="px-4 py-2 text-left font-semibold text-stone-700">Particular</th>
                        <th className="px-4 py-2 text-right font-semibold text-stone-700">Balance ({reportCurrency})</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      <tr className="bg-stone-200">
                        <td className="px-4 py-2 font-bold text-stone-800">ASSETS</td>
                        <td />
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold text-stone-700">Current Assets</td>
                        <td />
                      </tr>
                      <tr>
                        <td className="px-8 py-2 text-stone-700">Bank and Cash Accounts</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-800">
                          {Number(balanceSheetData?.assets?.cash_and_bank || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-8 py-2 text-stone-700">Receivables</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-800">
                          {Number(balanceSheetData?.assets?.receivables || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr className="bg-stone-50">
                        <td className="px-8 py-2 text-stone-700">Current Assets (Other)</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-500">0.000</td>
                      </tr>
                      <tr className="bg-stone-50">
                        <td className="px-8 py-2 text-stone-700">Prepayments</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-500">0.000</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold text-stone-800">Total Current Assets</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                          {Number(balanceSheetData?.assets?.total_assets || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr className="bg-stone-50">
                        <td className="px-4 py-2 text-stone-700">Plus Fixed Assets</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-500">0.000</td>
                      </tr>
                      <tr className="bg-stone-50">
                        <td className="px-4 py-2 text-stone-700">Plus Non-current Assets</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-500">0.000</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold text-stone-900">Total ASSETS</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                          {Number(balanceSheetData?.totals?.assets || 0).toFixed(3)}
                        </td>
                      </tr>

                      <tr className="bg-stone-200">
                        <td className="px-4 py-2 font-bold text-stone-800">LIABILITIES</td>
                        <td />
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold text-stone-700">Current Liabilities</td>
                        <td />
                      </tr>
                      <tr>
                        <td className="px-8 py-2 text-stone-700">Payables</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-800">
                          {Number(balanceSheetData?.liabilities?.payables || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold text-stone-800">Total Current Liabilities</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                          {Number(balanceSheetData?.liabilities?.total_liabilities || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr className="bg-stone-50">
                        <td className="px-4 py-2 text-stone-700">Plus Non-current Liabilities</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-500">0.000</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold text-stone-900">Total LIABILITIES</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                          {Number(balanceSheetData?.liabilities?.total_liabilities || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr className="bg-stone-200">
                        <td className="px-4 py-2 font-bold text-stone-800">EQUITY</td>
                        <td />
                      </tr>
                      <tr>
                        <td className="px-8 py-2 text-stone-700">Retained Earnings (Proxy)</td>
                        <td className="px-4 py-2 text-right font-mono text-stone-800">
                          {Number(balanceSheetData?.equity?.retained_earnings_proxy || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-bold text-stone-900">Total EQUITY</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                          {Number(balanceSheetData?.equity?.total_equity || 0).toFixed(3)}
                        </td>
                      </tr>
                      <tr className="border-t-2 border-stone-400 bg-amber-50">
                        <td className="px-4 py-2 font-bold text-stone-900">Total LIABILITIES + EQUITY</td>
                        <td className="px-4 py-2 text-right font-mono font-bold text-stone-900">
                          {Number(balanceSheetData?.totals?.liabilities_and_equity || 0).toFixed(3)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {financialSubTab === "cash-flow" && cashFlowData && (
                <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-4">
                  <h3 className="text-lg font-semibold text-stone-800">
                    Cash Flow ({cashFlowData.start_date} to {cashFlowData.end_date})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="rounded-lg bg-stone-50 p-4">
                      <p className="text-sm text-stone-600">Operating Cash</p>
                      <p className="text-lg font-semibold text-cyan-700">
                        {reportCurrency} {Number(cashFlowData?.totals?.net_operating_cash || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="rounded-lg bg-stone-50 p-4">
                      <p className="text-sm text-stone-600">Investing Cash</p>
                      <p className="text-lg font-semibold text-stone-700">
                        {reportCurrency} {Number(cashFlowData?.totals?.net_investing_cash || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="rounded-lg bg-stone-50 p-4">
                      <p className="text-sm text-stone-600">Financing Cash</p>
                      <p className="text-lg font-semibold text-amber-700">
                        {reportCurrency} {Number(cashFlowData?.totals?.net_financing_cash || 0).toFixed(2)}
                      </p>
                    </div>
                    <div className="rounded-lg bg-emerald-50 p-4">
                      <p className="text-sm text-stone-600">Net Cash Change</p>
                      <p className="text-lg font-semibold text-emerald-700">
                        {reportCurrency} {Number(cashFlowData?.totals?.net_cash_change || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {financialSubTab === "daily-operations" && dailyOperationsData && (
                <div className="bg-white rounded-lg border border-stone-300 overflow-hidden">
                  <div className="bg-black text-white px-4 py-2 font-bold text-center text-xl tracking-wide">
                    DAILY SALES SUMMARY
                    <span className="float-right text-base font-semibold">
                      {new Date(dailyOperationsData.report_date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-200 border-b border-stone-300">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Client Name</th>
                          <th className="px-3 py-2 text-left">Service</th>
                          <th className="px-3 py-2 text-left">Receipt Voucher</th>
                          <th className="px-3 py-2 text-right">Credit Sales OMR</th>
                          <th className="px-3 py-2 text-right">Bank Transfer OMR</th>
                          <th className="px-3 py-2 text-right">Cash Sales OMR</th>
                          <th className="px-3 py-2 text-right">Total OMR</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {(dailyOperationsData.daily_sales_rows || []).map((row) => (
                          <tr key={row.serial_no}>
                            <td className="px-3 py-1.5">{row.serial_no}</td>
                            <td className="px-3 py-1.5 font-medium">{row.client_name}</td>
                            <td className="px-3 py-1.5">{row.service}</td>
                            <td className="px-3 py-1.5">{row.receipt_voucher}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{Number(row.credit_sales_omr || 0).toFixed(3)}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{Number(row.bank_transfer_omr || 0).toFixed(3)}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{Number(row.cash_sales_omr || 0).toFixed(3)}</td>
                            <td className="px-3 py-1.5 text-right font-mono font-semibold">{Number(row.total_omr || 0).toFixed(3)}</td>
                          </tr>
                        ))}
                        <tr className="bg-black text-white font-bold">
                          <td className="px-3 py-2" colSpan={4}>TOTAL</td>
                          <td className="px-3 py-2 text-right font-mono">{Number(dailyOperationsData?.sales_totals?.credit_sales_omr || 0).toFixed(3)}</td>
                          <td className="px-3 py-2 text-right font-mono">{Number(dailyOperationsData?.sales_totals?.bank_transfer_omr || 0).toFixed(3)}</td>
                          <td className="px-3 py-2 text-right font-mono">{Number(dailyOperationsData?.sales_totals?.cash_sales_omr || 0).toFixed(3)}</td>
                          <td className="px-3 py-2 text-right font-mono">{Number(dailyOperationsData?.sales_totals?.total_omr || 0).toFixed(3)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-black text-white px-4 py-2 font-bold text-center text-xl tracking-wide mt-4">
                    Payments & Deposit
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-200 border-b border-stone-300">
                        <tr>
                          <th className="px-3 py-2 text-left">#</th>
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-right">Total CHQ</th>
                          <th className="px-3 py-2 text-right">Total OMR</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {(dailyOperationsData.payments_deposit_rows || []).map((row) => (
                          <tr key={`dep-${row.serial_no}`}>
                            <td className="px-3 py-1.5">{row.serial_no}</td>
                            <td className="px-3 py-1.5 font-medium">{row.item}</td>
                            <td className="px-3 py-1.5">{row.description}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{Number(row.total_chq || 0).toFixed(3)}</td>
                            <td className="px-3 py-1.5 text-right font-mono">{Number(row.total_omr || 0).toFixed(3)}</td>
                          </tr>
                        ))}
                        <tr className="bg-black text-white font-bold">
                          <td className="px-3 py-2" colSpan={4}>TOTAL</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {Number(dailyOperationsData.payments_deposit_total_omr || 0).toFixed(3)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-black text-white px-4 py-2 font-bold text-center text-xl tracking-wide mt-4">
                    FINAL AMOUNT TO BE DEPOSIT
                    <span className="float-right font-mono">
                      {Number(dailyOperationsData.final_amount_to_deposit || 0).toFixed(3)}
                    </span>
                  </div>
                </div>
              )}
              {activeTab === "financial" &&
                !loading &&
                (() => {
                  const hasDataByTab = {
                    "revenue-by-period": revenueByPeriodData.length > 0,
                    "revenue-by-salesperson": revenueBySalespersonData.length > 0,
                    "revenue-by-client": revenueByClientData.length > 0,
                    "revenue-by-service-type": revenueByServiceTypeData.length > 0,
                    "outstanding-aging": Boolean(outstandingAgingData),
                    "paid-invoices": Boolean(paidInvoicesData),
                    "expense-by-category": expenseByCategoryData.length > 0,
                    "pl-statement": Boolean(plStatementData),
                    "balance-sheet": Boolean(balanceSheetData),
                    "cash-flow": Boolean(cashFlowData),
                    "daily-operations": Boolean(dailyOperationsData),
                  };
                  return !Boolean(hasDataByTab[financialSubTab]);
                })() && (
                <p className="text-stone-500 py-8 text-center">Select options and click Generate Report.</p>
              )}
            </div>
          )}

          {/* ROI Analytics (GAP 2.3) */}
          {activeTab === "roi" && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={roiDateRange.start_date}
                    onChange={(e) => setRoiDateRange({ ...roiDateRange, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={roiDateRange.end_date}
                    onChange={(e) => setRoiDateRange({ ...roiDateRange, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Service type (optional)</label>
                  <input
                    type="text"
                    value={roiServiceType}
                    onChange={(e) => setRoiServiceType(e.target.value)}
                    placeholder="e.g. visa"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Salesperson ID (optional)</label>
                  <input
                    type="text"
                    value={roiSalesperson}
                    onChange={(e) => setRoiSalesperson(e.target.value)}
                    placeholder="User ID"
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <button
                  onClick={fetchRoi}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Generate Report"}
                </button>
              </div>

              {roiData && (
                <div className="space-y-6">
                  {roiData.summary && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="bg-stone-50 rounded-lg p-4">
                        <p className="text-sm text-stone-600">Revenue</p>
                        <p className="text-xl font-semibold">{reportCurrency} {roiData.summary.total_revenue?.toFixed(2)}</p>
                      </div>
                      <div className="bg-stone-50 rounded-lg p-4">
                        <p className="text-sm text-stone-600">Cost</p>
                        <p className="text-xl font-semibold">{reportCurrency} {roiData.summary.total_cost?.toFixed(2)}</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-4">
                        <p className="text-sm text-stone-600">Expenses</p>
                        <p className="text-xl font-semibold text-red-600">{reportCurrency} {roiData.summary.total_expenses?.toFixed(2)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <p className="text-sm text-stone-600">Net Profit</p>
                        <p className={`text-xl font-semibold ${roiData.summary.overall_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{reportCurrency} {roiData.summary.overall_profit?.toFixed(2)}</p>
                      </div>
                      <div className="bg-cyan-50 rounded-lg p-4">
                        <p className="text-sm text-stone-600">ROI %</p>
                        <p className="text-xl font-semibold text-cyan-600">{roiData.summary.roi_pct != null ? roiData.summary.roi_pct?.toFixed(1) + "%" : "—"}</p>
                      </div>
                    </div>
                  )}

                  {roiData.time_series && roiData.time_series.length > 0 && (
                    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                      <h3 className="text-lg font-semibold text-stone-800 p-4 border-b border-stone-200">Revenue & profit over time</h3>
                      <div className="overflow-x-auto max-h-64 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-stone-50 border-b border-stone-200 sticky top-0">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Period</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Cost</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Profit</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-200">
                            {roiData.time_series.map((r) => (
                              <tr key={r.period} className="hover:bg-stone-50">
                                <td className="px-4 py-3 font-medium text-stone-800">{r.period}</td>
                                <td className="px-4 py-3 text-right font-mono">{reportCurrency} {r.revenue?.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right font-mono text-stone-600">{reportCurrency} {r.cost?.toFixed(2)}</td>
                                <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{reportCurrency} {r.profit?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {roiData.profit_by_service_type && roiData.profit_by_service_type.length > 0 && (
                    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                      <h3 className="text-lg font-semibold text-stone-800 p-4 border-b border-stone-200">Profit by service type</h3>
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Service type</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Cost</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Profit</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">ROI %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {roiData.profit_by_service_type.map((r) => (
                            <tr key={r.service_type} className="hover:bg-stone-50">
                              <td className="px-4 py-3 font-medium text-stone-800">{r.service_type?.replace(/_/g, " ")}</td>
                              <td className="px-4 py-3 text-right font-mono">{reportCurrency} {r.revenue?.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right font-mono text-stone-600">{reportCurrency} {r.cost?.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right font-mono text-emerald-600">{reportCurrency} {r.profit?.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right font-mono">{r.roi_pct != null ? r.roi_pct?.toFixed(1) + "%" : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {roiData.profit_by_salesperson && roiData.profit_by_salesperson.length > 0 && (
                    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                      <h3 className="text-lg font-semibold text-stone-800 p-4 border-b border-stone-200">Profit by salesperson</h3>
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Salesperson</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Cost</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Profit</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">ROI %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {roiData.profit_by_salesperson.map((r) => (
                            <tr key={r.user_id} className="hover:bg-stone-50">
                              <td className="px-4 py-3 font-medium text-stone-800">{r.salesperson_name}</td>
                              <td className="px-4 py-3 text-right font-mono">{reportCurrency} {r.revenue?.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right font-mono text-stone-600">{reportCurrency} {r.cost?.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right font-mono text-emerald-600">{reportCurrency} {r.profit?.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right font-mono">{r.roi_pct != null ? r.roi_pct?.toFixed(1) + "%" : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {roiData.expense_distribution && roiData.expense_distribution.length > 0 && (
                    <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                      <h3 className="text-lg font-semibold text-stone-800 p-4 border-b border-stone-200">Expense distribution by category</h3>
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Category</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Amount ({reportCurrency})</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {roiData.expense_distribution.map((r) => (
                            <tr key={r.category} className="hover:bg-stone-50">
                              <td className="px-4 py-3 font-medium text-stone-800">{r.category}</td>
                              <td className="px-4 py-3 text-right font-mono">{reportCurrency} {r.total?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {activeTab === "roi" && !roiData && !loading && (
                <p className="text-stone-500 py-8 text-center">Select date range and click Generate Report.</p>
              )}
            </div>
          )}

          {/* PAX Reports */}
          {activeTab === "pax" && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={paxDateRange.start_date}
                    onChange={(e) => setPaxDateRange({ ...paxDateRange, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={paxDateRange.end_date}
                    onChange={(e) => setPaxDateRange({ ...paxDateRange, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                  />
                </div>
                <button
                  onClick={fetchPaxByService}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
                >
                  PAX by Service
                </button>
                <button
                  onClick={fetchRevenuePerPax}
                  disabled={loading}
                  className="px-6 py-2 bg-stone-600 hover:bg-stone-700 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
                >
                  Revenue per PAX
                </button>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-stone-600">Group by</label>
                  <select
                    value={paxTrendsGroupBy}
                    onChange={(e) => setPaxTrendsGroupBy(e.target.value)}
                    className="px-3 py-2 border border-stone-300 rounded-lg"
                  >
                    <option value="day">Day</option>
                    <option value="month">Month</option>
                    <option value="year">Year</option>
                  </select>
                </div>
                <button
                  onClick={fetchPaxTrends}
                  disabled={loading}
                  className="px-6 py-2 bg-stone-500 hover:bg-stone-600 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
                >
                  PAX Trends
                </button>
              </div>

              {paxByServiceData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <h3 className="text-lg font-semibold text-stone-800 p-4 border-b border-stone-200">PAX count by service type</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-stone-50 border-b border-stone-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Service type</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">PAX count</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {paxByServiceData.map((r) => (
                          <tr key={r.service_type} className="hover:bg-stone-50">
                            <td className="px-4 py-3 text-stone-800">{r.service_type || "—"}</td>
                            <td className="px-4 py-3 text-right font-medium">{r.pax_count ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {revenuePerPaxData && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-stone-50 rounded-lg p-4">
                    <p className="text-sm text-stone-600">Total revenue</p>
                    <p className="text-xl font-semibold">{reportCurrency} {revenuePerPaxData.total_revenue?.toFixed(2) ?? "0.00"}</p>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-4">
                    <p className="text-sm text-stone-600">Total PAX</p>
                    <p className="text-xl font-semibold">{revenuePerPaxData.total_pax ?? 0}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4">
                    <p className="text-sm text-stone-600">Revenue per PAX</p>
                    <p className="text-xl font-semibold text-emerald-700">
                      {revenuePerPaxData.revenue_per_pax != null ? `${reportCurrency} ${revenuePerPaxData.revenue_per_pax.toFixed(2)}` : "—"}
                    </p>
                  </div>
                  <div className="bg-cyan-50 rounded-lg p-4">
                    <p className="text-sm text-stone-600">Profit per PAX</p>
                    <p className="text-xl font-semibold text-cyan-700">
                      {revenuePerPaxData.profit_per_pax != null ? `${reportCurrency} ${revenuePerPaxData.profit_per_pax.toFixed(2)}` : "—"}
                    </p>
                  </div>
                </div>
              )}

              {paxTrendsData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <h3 className="text-lg font-semibold text-stone-800 p-4 border-b border-stone-200">PAX trends over time</h3>
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-stone-50 border-b border-stone-200 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Period</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">PAX count</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone-200">
                        {paxTrendsData.map((r) => (
                          <tr key={r.period} className="hover:bg-stone-50">
                            <td className="px-4 py-3 text-stone-800">{r.period}</td>
                            <td className="px-4 py-3 text-right font-medium">{r.pax_count ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "pax" && !loading && paxByServiceData.length === 0 && !revenuePerPaxData && paxTrendsData.length === 0 && (
                <p className="text-stone-500 py-8 text-center">Use the buttons above to load PAX by service, Revenue per PAX, or PAX trends.</p>
              )}
            </div>
          )}

          {/* Service Profitability Report */}
          {activeTab === "service-profitability" && (
            <div className="space-y-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={serviceProfitDateRange.start_date}
                      onChange={(e) => setServiceProfitDateRange({ ...serviceProfitDateRange, start_date: e.target.value })}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={serviceProfitDateRange.end_date}
                      onChange={(e) => setServiceProfitDateRange({ ...serviceProfitDateRange, end_date: e.target.value })}
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Salesperson (optional)</label>
                    <input
                      type="text"
                      value={serviceProfitSalesperson}
                      onChange={(e) => setServiceProfitSalesperson(e.target.value)}
                      placeholder="User ID"
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={fetchServiceProfitability}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Generate Report"}
                </button>
              </div>
              {serviceProfitData.length > 0 && (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-stone-50 border-b border-stone-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">Service Type</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Line Count</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Revenue</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Cost</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Gross Profit</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">Margin %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-200">
                      {serviceProfitData.map((row) => (
                        <tr key={row.service_type} className="hover:bg-stone-50">
                          <td className="px-4 py-3 font-medium text-stone-800">{row.service_type.replace(/_/g, " ")}</td>
                          <td className="px-4 py-3 text-right text-stone-600">{row.total_orders}</td>
                          <td className="px-4 py-3 text-right font-mono">{reportCurrency} {row.total_revenue.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-mono text-stone-600">{reportCurrency} {row.total_cost.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{reportCurrency} {row.gross_profit.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-mono">{row.margin_pct.toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {serviceProfitData.length === 0 && !loading && (
                <p className="text-stone-500 py-8 text-center">Select date range and click Generate Report. Data appears for approved invoices with cost/service_type on lines (e.g. from closed orders).</p>
              )}
            </div>
          )}

          {/* Income Status Report */}
          {activeTab === "income" && (
            <div className="space-y-6">
              <div className="flex gap-4 items-end">
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={incomeDateRange.start_date}
                      onChange={(e) =>
                        setIncomeDateRange({
                          ...incomeDateRange,
                          start_date: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={incomeDateRange.end_date}
                      onChange={(e) =>
                        setIncomeDateRange({
                          ...incomeDateRange,
                          end_date: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={fetchIncomeStatus}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Generate Report"}
                </button>
              </div>

              {incomeData.length > 0 && (
                <div className="space-y-4">
                  <div className="flex gap-4 justify-end">
                    <button
                      onClick={() => handleExportPDF("income")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileText size={18} />
                      Export PDF
                    </button>
                    <button
                      onClick={() => handleExportExcel("income")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileSpreadsheet size={18} />
                      Export Excel
                    </button>
                  </div>

                  <div className="bg-emerald-50 rounded-lg p-6 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-secondary">
                        Total Income:
                      </span>
                      <span className="text-2xl font-bold text-emerald-600">
                        {reportCurrency} {totalIncome.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Description
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Category
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {incomeData.map((item) => (
                            <tr key={item.id} className="hover:bg-stone-50">
                              <td className="px-4 py-3 text-sm">
                                {new Date(item.date).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {item.description}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">
                                  {item.category}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-emerald-600">
                                {reportCurrency} {parseFloat(item.amount).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expense Report */}
          {activeTab === "expenses" && (
            <div className="space-y-6">
              <div className="flex gap-4 items-end">
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={expenseDateRange.start_date}
                      onChange={(e) =>
                        setExpenseDateRange({
                          ...expenseDateRange,
                          start_date: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={expenseDateRange.end_date}
                      onChange={(e) =>
                        setExpenseDateRange({
                          ...expenseDateRange,
                          end_date: e.target.value,
                        })
                      }
                      className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={fetchExpenseReport}
                  disabled={loading}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Generate Report"}
                </button>
              </div>

              {expenseData.length > 0 && (
                <div className="space-y-4">
                  <div className="flex gap-4 justify-end">
                    <button
                      onClick={() => handleExportPDF("expenses")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileText size={18} />
                      Export PDF
                    </button>
                    <button
                      onClick={() => handleExportExcel("expenses")}
                      className="flex items-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors"
                    >
                      <FileSpreadsheet size={18} />
                      Export Excel
                    </button>
                  </div>

                  <div className="bg-red-50 rounded-lg p-6 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-secondary">
                        Total Expenses:
                      </span>
                      <span className="text-2xl font-bold text-red-600">
                        {reportCurrency} {totalExpenses.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-stone-50 border-b border-stone-200">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Description
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-stone-600 uppercase">
                              Category
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-stone-600 uppercase">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-200">
                          {expenseData.map((item) => (
                            <tr key={item.id} className="hover:bg-stone-50">
                              <td className="px-4 py-3 text-sm">
                                {new Date(item.date).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {item.description}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                                  {item.category}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-red-600">
                                {reportCurrency} {parseFloat(item.amount).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
